import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../../convex/functions/_generated/api';
import { adminSession, type AdminPrompt, type PromptCategory } from '@/lib/api';
import { friendlyError } from '@/lib/errors';

/**
 * /dashboard/prompts — admin curation of the Hinge-style profile
 * prompts surfaced in the Builder picker.
 *
 * Layout: one section per category (Story Time / Let's Chat About /
 * Voice First) with a row per prompt. Each row supports inline text
 * edit + an Active toggle. Soft-delete via `active: false` keeps
 * existing user answers rendering on their profiles while the
 * prompt disappears from the picker — see
 * src/convex/functions/prompts.ts:_getByIds for the hydration that
 * intentionally ignores the active flag.
 *
 * Categories are locked at the schema layer. Adding a new category
 * requires a code change to ProfilePromptCategory in the backend.
 */

const CATEGORY_ORDER: PromptCategory[] = [
  'story_time',
  'lets_chat_about',
  'voice_first',
];
const CATEGORY_LABELS: Record<PromptCategory, string> = {
  story_time: 'Story Time',
  lets_chat_about: "Let's Chat About",
  voice_first: 'Voice First',
};
const PROMPT_TEXT_MAX = 80;

export function PromptsPage() {
  const token = adminSession.getToken() ?? '';
  const data = useQuery(
    api.admin.prompts.listPrompts,
    token ? { token } : 'skip',
  );
  const createMut = useMutation(api.admin.prompts.createPrompt);
  const updateTextMut = useMutation(api.admin.prompts.updatePromptText);
  const setActiveMut = useMutation(api.admin.prompts.setPromptActive);

  const [busyId, setBusyId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const prompts: AdminPrompt[] | null = data
    ? (data.prompts as AdminPrompt[])
    : null;

  const grouped = useMemo(() => {
    const m: Record<PromptCategory, AdminPrompt[]> = {
      story_time: [],
      lets_chat_about: [],
      voice_first: [],
    };
    for (const p of prompts ?? []) m[p.category].push(p);
    return m;
  }, [prompts]);

  const handleCreate = async (category: PromptCategory, text: string) => {
    setErr(null);
    try {
      await createMut({ token, category, text });
    } catch (e) {
      setErr(friendlyError(e, 'createPrompt'));
    }
  };

  const handleUpdateText = async (id: string, text: string) => {
    setErr(null);
    setBusyId(id);
    try {
      await updateTextMut({ token, id: id as never, text });
    } catch (e) {
      setErr(friendlyError(e, 'updatePromptText'));
    } finally {
      setBusyId(null);
    }
  };

  const handleSetActive = async (id: string, next: boolean) => {
    setErr(null);
    setBusyId(id);
    try {
      await setActiveMut({ token, id: id as never, active: next });
    } catch (e) {
      setErr(friendlyError(e, 'setPromptActive'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <header style={{ marginBottom: 24 }}>
        <h2
          className="serif"
          style={{
            fontSize: 30,
            fontWeight: 500,
            fontStyle: 'italic',
            margin: 0,
          }}
        >
          Profile prompts
        </h2>
        <p
          style={{
            color: 'var(--ink-3)',
            fontSize: 13,
            marginTop: 6,
            maxWidth: 640,
          }}
        >
          Curate the prompts that appear in the Builder's picker. Each
          prompt is grouped under a category; up to 80 characters per
          prompt. Disabling a prompt hides it from the picker but keeps
          existing user answers rendering on their profiles.
        </p>
      </header>

      {err && (
        <div
          style={{
            background: 'rgba(208,64,48,0.08)',
            color: '#a8331f',
            padding: '10px 14px',
            borderRadius: 12,
            fontSize: 13,
            marginBottom: 16,
          }}
        >
          {err}
        </div>
      )}

      {prompts === null ? (
        <div style={{ color: 'var(--ink-3)', padding: 32, textAlign: 'center' }}>
          Loading…
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
          {CATEGORY_ORDER.map((cat) => (
            <CategorySection
              key={cat}
              category={cat}
              prompts={grouped[cat]}
              busyId={busyId}
              onCreate={(text) => void handleCreate(cat, text)}
              onUpdateText={(id, text) => void handleUpdateText(id, text)}
              onSetActive={(id, next) => void handleSetActive(id, next)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CategorySection({
  category,
  prompts,
  busyId,
  onCreate,
  onUpdateText,
  onSetActive,
}: {
  category: PromptCategory;
  prompts: AdminPrompt[];
  busyId: string | null;
  onCreate: (text: string) => void;
  onUpdateText: (id: string, text: string) => void;
  onSetActive: (id: string, next: boolean) => void;
}) {
  return (
    <section>
      <header
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 12,
          marginBottom: 12,
        }}
      >
        <h3
          className="serif"
          style={{
            fontSize: 20,
            fontStyle: 'italic',
            fontWeight: 500,
            margin: 0,
          }}
        >
          {CATEGORY_LABELS[category]}
        </h3>
        <span
          className="mono"
          style={{ fontSize: 11, color: 'var(--ink-3)', letterSpacing: 1 }}
        >
          {prompts.length} prompt{prompts.length === 1 ? '' : 's'}
        </span>
      </header>

      <NewPromptForm onSubmit={onCreate} />

      {prompts.length === 0 ? (
        <div
          style={{
            color: 'var(--ink-3)',
            fontSize: 13,
            padding: '16px 0',
            fontStyle: 'italic',
          }}
        >
          No prompts in this category yet. Add the first one above.
        </div>
      ) : (
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}
        >
          {prompts.map((p) => (
            <PromptRow
              key={p.id}
              prompt={p}
              busy={busyId === p.id}
              onUpdateText={(text) => onUpdateText(p.id, text)}
              onSetActive={(next) => onSetActive(p.id, next)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function NewPromptForm({ onSubmit }: { onSubmit: (text: string) => void }) {
  const [text, setText] = useState('');
  const remaining = PROMPT_TEXT_MAX - text.length;
  const trimmed = text.trim();
  const ok = trimmed.length > 0 && trimmed.length <= PROMPT_TEXT_MAX;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!ok) return;
        onSubmit(trimmed);
        setText('');
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        background: 'var(--surface)',
        borderRadius: 14,
        padding: '8px 12px',
        border: '1px dashed var(--line-strong)',
      }}
    >
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, PROMPT_TEXT_MAX))}
        placeholder="Add a new prompt…"
        maxLength={PROMPT_TEXT_MAX}
        style={{
          flex: 1,
          minWidth: 0,
          background: 'transparent',
          border: 'none',
          fontFamily: 'var(--font-ui)',
          fontSize: 14,
          color: 'var(--ink)',
          outline: 'none',
        }}
      />
      <span
        className="mono"
        style={{ fontSize: 10, color: 'var(--ink-3)', minWidth: 32, textAlign: 'right' }}
      >
        {remaining}
      </span>
      <button
        type="submit"
        disabled={!ok}
        style={{
          background: ok ? 'var(--coral)' : 'var(--chip)',
          color: ok ? '#fff' : 'var(--ink-3)',
          border: 'none',
          borderRadius: 100,
          padding: '6px 14px',
          fontSize: 13,
          fontWeight: 600,
          cursor: ok ? 'pointer' : 'not-allowed',
          fontFamily: 'var(--font-ui)',
        }}
      >
        Add
      </button>
    </form>
  );
}

function PromptRow({
  prompt,
  busy,
  onUpdateText,
  onSetActive,
}: {
  prompt: AdminPrompt;
  busy: boolean;
  onUpdateText: (text: string) => void;
  onSetActive: (next: boolean) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(prompt.text);

  // Re-sync the editor when prompts are reloaded from upstream.
  useEffect(() => {
    if (!editing) setDraft(prompt.text);
  }, [prompt.text, editing]);

  const saveEdit = () => {
    const trimmed = draft.trim();
    if (trimmed.length === 0 || trimmed.length > PROMPT_TEXT_MAX) {
      setDraft(prompt.text);
      setEditing(false);
      return;
    }
    if (trimmed !== prompt.text) {
      onUpdateText(trimmed);
    }
    setEditing(false);
  };

  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 14,
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        flexWrap: 'wrap',
        opacity: prompt.active ? 1 : 0.55,
      }}
    >
      {/* Small basis so the toggle stays beside the text on a phone. */}
      <div style={{ flex: '1 1 200px', minWidth: 0 }}>
        {editing ? (
          <input
            autoFocus
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, PROMPT_TEXT_MAX))}
            onBlur={saveEdit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                saveEdit();
              }
              if (e.key === 'Escape') {
                setDraft(prompt.text);
                setEditing(false);
              }
            }}
            maxLength={PROMPT_TEXT_MAX}
            style={{
              width: '100%',
              background: 'var(--bg)',
              border: '1.5px solid var(--coral)',
              borderRadius: 10,
              padding: '6px 10px',
              fontFamily: 'var(--font-serif)',
              fontStyle: 'italic',
              fontSize: 15,
              color: 'var(--ink)',
              outline: 'none',
            }}
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            style={{
              all: 'unset',
              cursor: 'pointer',
              fontFamily: 'var(--font-serif)',
              fontStyle: 'italic',
              fontSize: 15,
              color: 'var(--ink)',
            }}
            title="Click to edit"
          >
            {prompt.text}
          </button>
        )}
        <div
          className="mono"
          style={{
            fontSize: 10,
            color: 'var(--ink-3)',
            letterSpacing: 1,
            marginTop: 6,
          }}
        >
          ID {String(prompt.id).slice(-8)} · added{' '}
          {new Date(prompt.createdAt).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
          })}
        </div>
      </div>
      <button
        type="button"
        onClick={() => onSetActive(!prompt.active)}
        disabled={busy}
        style={{
          background: prompt.active ? 'var(--coral)' : 'var(--chip)',
          color: prompt.active ? '#fff' : 'var(--ink)',
          border: 'none',
          borderRadius: 100,
          padding: '8px 18px',
          fontSize: 13,
          fontWeight: 700,
          cursor: busy ? 'wait' : 'pointer',
          opacity: busy ? 0.6 : 1,
          fontFamily: 'var(--font-ui)',
          minWidth: 80,
        }}
        title={
          prompt.active
            ? 'Disable — hides from picker, keeps existing user answers.'
            : 'Enable — reappears in the Builder picker.'
        }
      >
        {busy ? '…' : prompt.active ? 'ACTIVE' : 'DISABLED'}
      </button>
    </div>
  );
}
